import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DoctoralCommitteeMembershipSelectControl } from '@/module/Nomen/DoctoralCommitteeMembership/DoctoralCommitteeMembershipSelectControl';

import {
  AuthorListControl,
  Content,
  EnumSelectControl,
  Form,
  FormSubmit,
  NumberInputControl,
  TextInputControl,
} from '@/core/components';

import { DoctoralCommitteeSchema } from './model/DoctoralCommitteeSchema';

interface DoctoralCommitteeFormProps extends FormSubmit<DoctoralCommitteeSchema> {
  title: string;
}

export function DoctoralCommitteeForm(props: DoctoralCommitteeFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<DoctoralCommitteeSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  const selectedType = useWatch({
    control: form.control,
    name: 'type',
  });

  useEffect(() => {
    if (selectedType === 'GUIDANCE') {
      form.setValue('year', null);
    } else if (selectedType === 'DEFENDING' || selectedType === 'ABILITATION_SUPPORT') {
      form.setValue('startYear', null);
      form.setValue('endYear', null);
    }
  }, [selectedType, form]);

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <Group align="flex-end">
              <EnumSelectControl<DoctoralCommitteeSchema>
                name="type"
                label={t('doctoralCommittees.form.fields.type')}
                isSubmitting={isSubmitting}
                options={[
                  { value: 'GUIDANCE', label: t('doctoralCommittees.typeEnum.GUIDANCE') },
                  { value: 'DEFENDING', label: t('doctoralCommittees.typeEnum.DEFENDING') },
                  { value: 'ABILITATION_SUPPORT', label: t('doctoralCommittees.typeEnum.ABILITATION_SUPPORT') },
                ]}
              />
            </Group>

            {selectedType === 'GUIDANCE' && (
              <Group align="flex-end">
                <NumberInputControl<DoctoralCommitteeSchema>
                  name="startYear"
                  label={t('doctoralCommittees.form.fields.startYear')}
                  isSubmitting={isSubmitting}
                  allowDecimal={false}
                  flex={0.5}
                />
                <NumberInputControl<DoctoralCommitteeSchema>
                  name="endYear"
                  label={t('doctoralCommittees.form.fields.endYear')}
                  isSubmitting={isSubmitting}
                  allowDecimal={false}
                  flex={0.5}
                />
              </Group>
            )}

            {(selectedType === 'DEFENDING' || selectedType === 'ABILITATION_SUPPORT') && (
              <Group align="flex-end">
                <NumberInputControl<DoctoralCommitteeSchema>
                  name="year"
                  label={t('doctoralCommittees.form.fields.year')}
                  isSubmitting={isSubmitting}
                  allowDecimal={false}
                  flex={0.4}
                />
              </Group>
            )}
            <TextInputControl<DoctoralCommitteeSchema>
              name="candidateName"
              label={t('doctoralCommittees.form.fields.candidateName')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<DoctoralCommitteeSchema>
              name="doctoralSchoolName"
              label={t('doctoralCommittees.form.fields.doctoralSchoolName')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<DoctoralCommitteeSchema>
              name="coordinatorName"
              label={t('doctoralCommittees.form.fields.coordinatorName')}
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<DoctoralCommitteeSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <DoctoralCommitteeMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
